<template>
	<BaseVcxLayout>
		<ArticleContent v-if="article" :article="article" :loading="loading" :placehold-theme="'dark'" />

		<template #footer>
			<BasePlaceHolder
				v-if="article?.flatFooterDisclaimers"
				:line-count="3"
				:placehold="loading"
				:theme="'dark'"
				:class="{ container: loading }"
			>
				<RichTextComponent :blocks="article?.flatFooterDisclaimers" />
			</BasePlaceHolder>
		</template>
	</BaseVcxLayout>
</template>

<script setup lang="ts">
import { useHead } from '@unhead/vue';
import { onUnmounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { getSanityBlogArticle } from '@api/sanity/graphql/blog-education-center';
import ArticleContent from '@components/blog/article-content.vue';
import RichTextComponent from '@components/sanity/rich-text-component.vue';
import { appCloudfrontPath } from '@constants';
import { SanityArticle } from 'types/sanity/education-center';

const route = useRoute();
const router = useRouter();

const headControl = useHead({
	script: [`window.prerenderReady = false;`]
});

const loading = ref(true);

const article = ref<SanityArticle | null>(null);

async function setup(): Promise<void> {
	try {
		const sanityArticle = await getSanityBlogArticle(route, 'article-view');
		article.value = sanityArticle;
	} catch {
		await router.push({ path: '/404' });
	}

	if (article.value) {
		headControl?.patch({
			title: article.value.title,
			meta: [
				{ property: 'og:title', content: article.value.title },
				{ name: 'description', content: article.value?.abstract ?? '' },
				{ property: 'og:description', content: article.value?.abstract ?? '' },
				{
					property: 'og:image',
					content:
						article.value.coverImage && article.value.coverImage.asset.url
							? article.value.coverImage.asset.url
							: `${appCloudfrontPath}/images/global/Fundrise-og-image.jpg`
				}
			],
			script: [`window.prerenderReady = true;`]
		});
	} else {
		await router.push({
			path: '/404'
		});
	}

	loading.value = false;
}

setup();

onUnmounted(() => {
	headControl?.dispose();
});
</script>

<style lang="scss">
@use '../../styles/vcx/responsive.scss' as *;

.vcx .vcx-page__content {
	.disclaimer {
		color: var(--vcx-content-inverse-tertiary);
	}

	.article-body h1,
	.article-body h2,
	.article-body h3,
	.article-body h4,
	.article-body h5,
	.article-body h6,
	.heading-1,
	.heading-2,
	.heading-3,
	.heading-4,
	.heading-5,
	.heading-6,
	.blockquote p,
	.article-body blockquote p {
		font-family: var(--vcx-font-heading);
		font-weight: var(--vcx-font-weight-bold);
	}

	.key-takeaways-module,
	.border-gray-20,
	.module {
		border-color: var(--vcx-border-dark);
	}

	.key-takeaways-module .ul li + li,
	.sanity-rich-text-container ul li,
	.sanity-rich-text-container ol li {
		margin-top: 1rem;
	}

	.article-body {
		.sanity-rich-text-component {
			@include vcx-media-lg {
				margin-top: 3rem;
				margin-bottom: 3rem;
			}
		}

		blockquote {
			margin: 2rem auto;
			padding: 0 2rem;

			@include vcx-media-lg {
				margin: 3rem auto;
				padding: 0 3rem;
			}
		}
	}

	.text-color-content-tertiary,
	.article-body .figcaption {
		color: var(--vcx-content-inverse-tertiary);
	}

	.article-cta {
		background-color: var(--vcx-gray-80);

		.cta-link--rust,
		.text-color-content-secondary,
		.third-party-auth .divider-text-primary {
			color: var(--vcx-content-inverse-tertiary);
		}

		.cta-link--rust:hover,
		.cta-link--rust:focus {
			color: var(--vcx-brand-primary);
		}

		.third-party-auth .divider-primary {
			border-top: 1px solid var(--vcx-content-inverse-tertiary);
		}
	}

	.bg-color-white,
	.bg-color-greige,
	.module {
		background-color: var(--vcx-bg-dark);

		.text-color-content-secondary {
			color: var(--vcx-content-inverse-tertiary);
		}
	}

	.blockquote p,
	.article-body blockquote p {
		color: var(--vcx-content-inverse-secondary);
	}

	.article-list-item a {
		color: var(--vcx-content-inverse);
	}

	.text-link,
	.article-body a:not(.button):not(.cta-link):not(.cta-link--rust) {
		color: var(--vcx-brand-primary);
	}
}
</style>
